/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  useRouteError
} from 'react-router';
import { Loader } from '@link/share/components';
import { INBOX_ENABLED } from '@link/share/constants';
import { NotFound, PrivacyPolicy, LegalNotice } from '@link/share/views';
import {
  BaseLayout,
  ActivityLayout,
  PlanLayout,
  BackendLayout,
  AllotmentLayout
} from '@link/supplier/layouts';
import PrivateRoute from './PrivateRoute';

const isProd = import.meta.env.VITE_APP_SUPPLIER_ENV === 'production';

const Dashboard = lazy(() => import('@link/supplier/views/Dashboard'));
const Activities = lazy(() => import('@link/supplier/views/Activities/list'));
const Login = lazy(() => import('@link/supplier/views/Login'));
const PasswordReset = lazy(() => import('@link/supplier/views/PasswordReset'));
const ContractsList = lazy(() => import('@link/supplier/views/Contracts/List'));
const ContractsDetail = lazy(
  () => import('@link/supplier/views/Contracts/Detail')
);
const AccountDetail = lazy(
  () => import('@link/supplier/views/Supplier/AccountDetail')
);
const AccountList = lazy(
  () => import('@link/supplier/views/Supplier/AccountList')
);
const SupplierDetail = lazy(
  () => import('@link/supplier/views/Supplier/SupplierDetail')
);
const AllotmentSettings = lazy(
  () => import('@link/supplier/views/Activities/AllotmentSettings')
);
const AllotmentSetting = lazy(
  () => import('@link/supplier/views/AllotmentSetting/Setting')
);
const AllotmentCalendar = lazy(
  () => import('@link/supplier/views/AllotmentSetting/Calendar')
);
const AllotmentException = lazy(
  () => import('@link/supplier/views/AllotmentSetting/ExceptionSetting')
);

const AllotmentSegmentId = lazy(
  () => import('@link/supplier/views/AllotmentSetting/SegmentId')
);

const AllotmentOtaGroup = lazy(
  () => import('@link/supplier/views/AllotmentSetting/OtaGroupSetting')
);

const AllotmentBackend = lazy(
  () => import('@link/supplier/views/AllotmentSetting/BackendBundle')
);

const BackendList = lazy(
  () => import('@link/supplier/views/Activities/BackendList')
);
const ChangePassword = lazy(
  () => import('@link/supplier/views/ChangePassword')
);
const ActivityInfo = lazy(() => import('@link/supplier/views/Activity/info'));
const AcvitityDetail = lazy(
  () => import('@link/supplier/views/Activity/Detail')
);
const AcvitityAgent = lazy(() => import('@link/supplier/views/Activity/Agent'));
const AcvitityI18n = lazy(() => import('@link/supplier/views/Activity/I18n'));
const Memo = lazy(() => import('@link/supplier/views/Activity/Memo'));
const EditLog = lazy(() => import('@link/supplier/views/Activity/EditLog'));
const PlanInfo = lazy(() => import('@link/supplier/views/Plan/Info'));
const PlanDetail = lazy(() => import('@link/supplier/views/Plan/Detail'));
const PlanBooking = lazy(() => import('@link/supplier/views/Plan/Booking'));
const PlanI18n = lazy(() => import('@link/supplier/views/Plan/I18n'));
const PlanPrice = lazy(() => import('@link/supplier/views/Plan/Price'));
const AdminActivity = lazy(
  () => import('@link/supplier/views/Activity/Admin/Activity')
);
const SetCode = lazy(
  () => import('@link/supplier/views/Activity/Admin/SetCode')
);
const BackendInventory = lazy(
  () => import('@link/supplier/views/BackendSetting/Inventory')
);
const BackendCalendar = lazy(
  () => import('@link/supplier/views/BackendSetting/Calendar')
);
const BookingList = lazy(() => import('@link/supplier/views/Booking/List'));
const BookingDetail = lazy(() => import('@link/supplier/views/Booking/Detail'));
const BookingDiscrepancies = lazy(
  () => import('@link/supplier/views/Booking/Discrepancies')
);
const BookingCancel = lazy(() => import('@link/supplier/views/Booking/Cancel'));
const BookingTodos = lazy(() => import('@link/supplier/views/Booking/Todos'));

const BulkOrderBookingList = lazy(
  () => import('@link/supplier/views/BulkOrder/BookingList')
);
const BulkOrderBookingDetail = lazy(
  () => import('@link/supplier/views/BulkOrder/BookingDetail')
);

const FinanceHistory = lazy(
  () => import('@link/supplier/views/Finance/History')
);
const FinanceDiscrepancies = lazy(
  () => import('@link/supplier/views/Finance/Discrepancies')
);
const FinanceTransactions = lazy(
  () => import('@link/supplier/views/Finance/Transactions')
);
const FinanceChange = lazy(() => import('@link/supplier/views/Finance/Change'));
const DataExport = lazy(() => import('@link/supplier/views/DataExport'));
// const FinanceAddPayment = lazy(
//   () => import('@link/supplier/views/Finance/AddPayment')
// );

const ActivityPreview = lazy(
  () => import('@link/supplier/views/Activities/ActivityPreview')
);

const NotificationsInbox = lazy(
  () => import('@link/supplier/views/Notifications/Inbox')
);

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => {
  return <Suspense fallback={<Loader />}>{view}</Suspense>;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route element={<PrivateRoute />}>
        <Route path="/" element={<BaseLayout />}>
          <Route index element={<Navigate to="activities" replace />} />
          <Route path="dashboard" element={lazyView(<Dashboard />)} />
          <Route path="contracts">
            <Route index element={lazyView(<ContractsList />)} />
            <Route path=":contractId" element={lazyView(<ContractsDetail />)} />
          </Route>
          <Route path="supplier">
            <Route path="detail" element={lazyView(<SupplierDetail />)}></Route>
            <Route path="accounts" element={lazyView(<AccountList />)}></Route>
            <Route
              path="accounts/:accountId"
              handle={{ hideSupplierSelect: true }}
              element={lazyView(<AccountDetail />)}
            />
            <Route
              path="accounts/new"
              element={lazyView(<AccountDetail />)}
              handle={{ hideSupplierSelect: true }}
            />
          </Route>
          <Route path="activities" element={lazyView(<Activities />)} />
          <Route
            path="/allotment-settings"
            element={lazyView(<AllotmentSettings />)}
          />
          <Route path="/backend-settings" element={lazyView(<BackendList />)} />
          <Route
            path="/change-password"
            element={lazyView(<ChangePassword />)}
          />
          <Route path="book">
            <Route path="booking" element={lazyView(<BookingList />)} />
            <Route path="booking/:bookingCustomId">
              <Route index element={lazyView(<BookingDetail />)} />
              <Route
                path="discrepancies/:bookingId"
                element={lazyView(<BookingDiscrepancies />)}
              />
              <Route
                path="cancel/:bookingId"
                element={lazyView(<BookingCancel />)}
              />
            </Route>
            <Route path="todos" element={lazyView(<BookingTodos />)} />
          </Route>
          <Route path="bulk-order">
            <Route path="list" element={lazyView(<BulkOrderBookingList />)} />
            <Route
              path="list/:bulkOrderId"
              element={lazyView(<BulkOrderBookingDetail />)}
            />
          </Route>
          {INBOX_ENABLED && (
            <Route path="notifications">
              <Route path="inbox" element={lazyView(<NotificationsInbox />)} />
            </Route>
          )}
          <Route path="finance">
            <Route
              path="discrepancies"
              element={lazyView(<FinanceDiscrepancies />)}
            />
            <Route
              path="transactions"
              element={lazyView(<FinanceTransactions />)}
            />
            <Route
              path="transactions/:invoiceNumber"
              element={lazyView(<FinanceChange />)}
            />
            <Route path="history" element={lazyView(<FinanceHistory />)} />
          </Route>
          {/* データ出力：本番环境暂不开放 */}
          {!isProd && (
            <Route path="data-export" element={lazyView(<DataExport />)} />
          )}
        </Route>
        <Route
          path="activities/:activityId"
          element={<ActivityLayout />}
          handle={{ hideSupplierSelect: true }}
        >
          <Route index element={<Navigate to={'info'} replace />} />
          <Route path="info" element={lazyView(<ActivityInfo />)} />
          <Route path="detail" element={lazyView(<AcvitityDetail />)} />
          <Route path="agent" element={lazyView(<AcvitityAgent />)} />
          <Route path="i18n" element={lazyView(<AcvitityI18n />)} />
          <Route path=":planId" element={<PlanLayout />}>
            <Route path="info" element={lazyView(<PlanInfo />)} />
            <Route path="booking" element={lazyView(<PlanBooking />)} />
            <Route path="detail" element={lazyView(<PlanDetail />)} />
            <Route path="i18n" element={lazyView(<PlanI18n />)} />
            <Route path="price" element={lazyView(<PlanPrice />)} />
          </Route>
          <Route path="admin/activity" element={lazyView(<AdminActivity />)} />
          <Route path="admin" element={<PlanLayout />}>
            <Route path=":planId" element={lazyView(<SetCode />)} />
          </Route>
          <Route path="memo" element={lazyView(<Memo />)} />
          <Route path="editLog" element={lazyView(<EditLog />)} />
        </Route>
        <Route path="/allotment-settings/:poolId" element={<AllotmentLayout />}>
          <Route index element={<Navigate to="setting" replace />} />
          <Route
            path="setting"
            element={lazyView(<AllotmentSetting />)}
            handle={{ breadcrumb: 'layouts.sidebar.allotmentSetting' }}
          />
          <Route
            path="calendar"
            element={lazyView(<AllotmentCalendar />)}
            handle={{ breadcrumb: 'layouts.sidebar.allotmentCalendar' }}
          />
          <Route
            path="exception"
            element={lazyView(<AllotmentException />)}
            handle={{ breadcrumb: 'layouts.sidebar.allotmentException' }}
          />
          <Route
            path="segmentId"
            element={lazyView(<AllotmentSegmentId />)}
            handle={{ breadcrumb: 'layouts.sidebar.allotmentSegmentId' }}
          />
          <Route
            path="otaGroup"
            element={lazyView(<AllotmentOtaGroup />)}
            handle={{ breadcrumb: 'layouts.sidebar.allotmentOtaGroup' }}
          />
          <Route
            path="backend"
            element={lazyView(<AllotmentBackend />)}
            handle={{ breadcrumb: 'layouts.sidebar.allotmentBackend' }}
          />
        </Route>
        <Route
          path={'backend-settings/:poolId'}
          element={<BackendLayout />}
          handle={{ hideSupplierSelect: true }}
        >
          <Route index element={<Navigate to={'inventory'} replace />} />
          <Route
            path={'inventory'}
            element={lazyView(<BackendInventory />)}
            handle={{ breadcrumb: 'layouts.sidebar.backendInventory' }}
          />
          <Route
            path={'calendar'}
            element={lazyView(<BackendCalendar />)}
            handle={{ breadcrumb: 'layouts.sidebar.backendCalendar' }}
          />
        </Route>
        <Route
          path="/activity-preview"
          element={lazyView(<ActivityPreview />)}
        />
      </Route>
      <Route path="/login" element={lazyView(<Login />)} />
      <Route path="/reset-password" element={lazyView(<PasswordReset />)} />
      <Route path="/privacy-policy" element={lazyView(<PrivacyPolicy />)} />
      <Route path="/legal-notice" element={lazyView(<LegalNotice />)} />
      <Route path="*" element={lazyView(<NotFound />)} />
    </Route>
  )
);

export default router;
